/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { Button, CrossRemoveSVG, PageTitle } from '~/shared/ui'
import { ModalWindow } from '~/shared/ui/ModalWindow'
import { PropsModalWindow } from '~/shared/ui/ModalWindow/type'
import {
  getBlobErrorMessage,
  prepareBlobTab,
  printBlobInNewTab,
  useToast,
} from '~/shared/lib'
import { useStore } from '~/app/store'
import { Input } from '~/shared/ui/input'
import { SVGDone } from '~/shared/ui/SVG/SVGDone'

interface ValidateError {
  validate: boolean
  message: string
}

const IDENTIFICATION_CODE_PATTERN = /^01\d{14}21[\x21-\x7E]{13}$/

interface ModalMandatoryMarkFieldProps extends PropsModalWindow {
  capsLockEnabled?: boolean
  onLastItemChecked?: () => void
}

export const ModalMandatorymarkField = observer(
  ({
    isOpen,
    onClose,
    id,
    orderId,
    originalData: mandatoryMarkField,
    capsLockEnabled = false,
    onLastItemChecked,
  }: ModalMandatoryMarkFieldProps) => {
    const { t } = useTranslation(['common', 'delivery'])
    const toast = useToast()
    const { deliveryStore } = useStore()
    const {
      setInputmandatoryMark,
      inputMandatoryMark,
      clearInputmandatoryMark,
    } = deliveryStore
    const [validate, setValidate] = useState<null | ValidateError>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const validationRequestRef = useRef<symbol | null>(null)

    useEffect(() => {
      if (isOpen && !capsLockEnabled && inputRef.current) {
        inputRef.current.focus()
      }
    }, [isOpen, capsLockEnabled])

    useEffect(() => {
      validationRequestRef.current = null
    }, [id, isOpen, orderId])

    const validateInput = (value: string): ValidateError | null => {
      const trimmedValue = value.trimStart()

      if (trimmedValue === 'zzz') {
        return null
      }

      if (!trimmedValue) {
        return { validate: false, message: t('requiredField') }
      }
      if (trimmedValue.length <= 80) {
        return {
          validate: false,
          message: t('delivery:valueMustBeLongerThan80'),
        }
      }
      if (/[а-яА-ЯёЁ]/.test(trimmedValue)) {
        return {
          validate: false,
          message: t('delivery:fieldMustNotContainCyrillic'),
        }
      }
      return null
    }

    const reprintMandatoryMark = async (identificationCode: string) => {
      if (!id || !orderId) return

      const printTab = prepareBlobTab()
      if (!printTab) {
        setValidate({
          validate: false,
          message: 'Разрешите всплывающие окна для повторной печати КИЗ.',
        })
        return
      }

      try {
        const pdf = await deliveryStore.reprintMandatoryMark(orderId, {
          itemId: id,
          identificationCode,
        })
        await printBlobInNewTab(pdf, printTab)
        clearInputmandatoryMark()
        setValidate(null)
        if (!capsLockEnabled) inputRef.current?.focus()
        toast.open(
          'Этикетка КИЗ отправлена на печать. Отсканируйте её повторно.',
          'success',
        )
      } catch (error) {
        printTab.close()
        setValidate({
          validate: false,
          message: await getBlobErrorMessage(error),
        })
      }
    }

    const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      if (capsLockEnabled || validationRequestRef.current) return

      const validationError = validateInput(inputMandatoryMark)
      if (validationError) {
        const identificationCode = inputMandatoryMark.trimStart()
        if (IDENTIFICATION_CODE_PATTERN.test(identificationCode)) {
          await reprintMandatoryMark(identificationCode)
          return
        }

        setValidate(validationError)
        return
      }

      if (!id || !orderId) return

      const shouldTriggerAutoPrint =
        deliveryStore.itemsOrderById.filter((item) => !item.checked).length ===
          1 &&
        deliveryStore.itemsOrderById.some(
          (item) => item.id === id && !item.checked,
        )

      deliveryStore.setOrderItems(
        id,
        'markValue',
        deliveryStore.inputMandatoryMark,
      )

      const requestToken = Symbol('mandatory-mark-validation')
      validationRequestRef.current = requestToken

      deliveryStore
        .validateMarks(orderId, {
          itemId: id,
          [mandatoryMarkField]: deliveryStore.inputMandatoryMark,
        })
        .then(() => {
          if (validationRequestRef.current !== requestToken) return
          toast.open(t('delivery:mandatoryMarkReadSuccess'), 'success')
          deliveryStore.setOrderItems(id, 'checked', true)
          if (shouldTriggerAutoPrint) {
            onLastItemChecked?.()
          }
          clearInputmandatoryMark()
          setValidate(null)
          onClose(e)
        })
        .catch((error: ValidateError) => {
          if (validationRequestRef.current !== requestToken) return
          setValidate(error)
        })
        .finally(() => {
          if (validationRequestRef.current === requestToken) {
            validationRequestRef.current = null
          }
        })
    }

    const handleClose = (e?: React.MouseEvent | Event) => {
      validationRequestRef.current = null
      clearInputmandatoryMark()
      setValidate(null)
      onClose(e)
    }

    const statusMess = validate?.message || ''

    return (
      <ModalWindow onClose={handleClose} isOpen={isOpen} maxWidth>
        <div className="flex flex-col gap-3">
          <div className="flex justify-between py-3 w-[400px]">
            <PageTitle title={t('anHonestSign')} size="text-[20px]" />
            <CrossRemoveSVG onChange={handleClose} size="24" />
          </div>
          <span className="text-[#707070]">
            {t('delivery:productSubjectToMandatoryMarking')}
          </span>
          <div>
            <Input
              ref={inputRef}
              id={`${id}-mandatory-mark`}
              labelType="floating"
              label={t('anHonestSign')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setInputmandatoryMark(e.target.value)
                setValidate(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !capsLockEnabled) {
                  handleConfirm(e as any)
                }
              }}
              disabled={capsLockEnabled}
              status={validate ? 'error' : null}
              helperText={statusMess}
              value={deliveryStore.inputMandatoryMark}
            />
          </div>
        </div>

        <div className="flex items-center justify-center p-6 space-x-2">
          <Button intent="secondary" onClick={handleClose} type="button">
            {t('Cancel2')}
          </Button>
          <Button
            intent="success"
            isLoading={deliveryStore.loaders.mandatoryMarkReprint}
            onClick={handleConfirm}
          >
            <SVGDone color="white" size="24" />
            {t('Confirm')}
          </Button>
        </div>
      </ModalWindow>
    )
  },
)
